/**
 * Browser game client: connection, clock, prediction of the local fighter,
 * reconciliation from acknowledged inputs, interpolation of everyone else.
 */
import {
  applyFighterSnapshot,
  decodeSnapshot,
  encodeInputs,
  type FighterSnapshot,
  parseServerControl,
  type ServerControl,
  type Snapshot,
  type WaitingMessage,
  WIRE_HASH,
} from "@meleeroyale/protocol";
import { isFighterCount, PROTOCOL_VERSION } from "@meleeroyale/shared";
import {
  createMatch,
  eyeLocal,
  type FighterState,
  type MatchPhase,
  type MatchState,
  type PlayerInput,
  poseFighter,
  rotateY,
  SIM_HASH,
  step,
  TICK_RATE,
  type Vec3,
  type Volume,
  wrapAngle,
  yawFromRadians,
} from "@meleeroyale/sim";
import { convergeAim } from "../arena/camera-rig.ts";
import type { Controls } from "../input/controls.ts";

export interface Telegraph {
  chargeLimb: number | null;
  chargeLevel: number;
  throwingArm: boolean;
  throwingLeg: boolean;
  windupArm: boolean;
  windupLeg: boolean;
  parryArm: number | null;
  staggered: boolean;
}

export interface RenderFighter {
  id: number;
  nickname: string;
  alive: boolean;
  isBot: boolean;
  isLocal: boolean;
  guard: boolean;
  exhausted: boolean;
  telegraph: Telegraph;
  pos: Vec3;
  facing: number;
  duck: number;
  lean: number;
  stance: number;
  twist: number;
  southpaw: boolean;
  checking: boolean;
  /** The zone the local fighter covers (0 head, 1 body, 2 legs), or -1; always -1 for others. */
  coverZone: number;
  /** The local fighter's combo: strikes started and presses waiting; zeros for others. */
  combo: { chain: number; queued: number };
  handL: Vec3;
  handR: Vec3;
  footL: Vec3;
  footR: Vec3;
  hp: number[];
  stamina: number;
  /** Damage this fighter's strikes have done this match (server total). */
  damageDealt: number;
}

export interface ClientView {
  connected: boolean;
  error: string | null;
  /** True until the first snapshot: the room is waiting for fighters. */
  waiting: boolean;
  phase: MatchPhase;
  tick: number;
  clockTicks: number;
  fightStartTick: number;
  cage: { r: number; targetR: number; phase: number; warning: boolean };
  myId: number;
  winner: number;
  rttMs: number;
  fighters: RenderFighter[];
}

export interface GameEvent {
  at: number;
  text: string;
}

/** The room before the match: who is in, and whether it can start. */
export interface WaitingView {
  connected: boolean;
  /** True once the socket closed (the room expired, emptied or the network dropped). */
  closed: boolean;
  error: string | null;
  info: WaitingMessage | null;
  /** performance.now() when `info` arrived, to count `closesIn` down locally. */
  infoAt: number;
}

/** How far in the past remote fighters are rendered (interpolation delay). */
export const RENDER_DELAY_MS = 100;
const INTERP_MS = RENDER_DELAY_MS;
const MAX_PENDING = 64;
const MAX_LEAD = 10;
/** Ticks the client may run in one timer callback to catch up (a stalled tab does not burst). */
const MAX_CATCHUP = 3;

interface BufferedSnapshot {
  receivedAt: number;
  snapshot: Snapshot;
}

export class GameClient {
  private ws: WebSocket | null = null;
  private authoritative: MatchState | null = null;
  private predicted: MatchState | null = null;
  private buffer: BufferedSnapshot[] = [];
  private pending: PlayerInput[] = [];
  private localTick = 0;
  private lastAck = 0;
  private loop: ReturnType<typeof setInterval> | null = null;
  private nextTickAt = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<() => void>();
  private eventListeners = new Set<(msg: ServerControl) => void>();
  private rttMs = 0;
  private error: string | null = null;
  private connected = false;
  private closed = false;
  private waiting: WaitingMessage | null = null;
  private waitingAt = 0;
  myId = -1;
  fightStartTick = 0;
  private readonly controls: Controls;
  private readonly url: string;
  private readonly token: string;
  private readonly nickname: string;

  constructor(options: { url: string; token: string; nickname: string; controls: Controls }) {
    this.url = options.url;
    this.token = options.token;
    this.nickname = options.nickname;
    this.controls = options.controls;
  }

  connect(): void {
    const ws = new WebSocket(this.url);
    ws.binaryType = "arraybuffer";
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      ws.send(
        JSON.stringify({
          t: "join",
          protocol: PROTOCOL_VERSION,
          simHash: SIM_HASH,
          wire: WIRE_HASH,
          nickname: this.nickname,
          token: this.token,
        }),
      );
      this.startPings();
      this.notify();
    };
    ws.onclose = () => {
      this.connected = false;
      this.closed = true;
      this.stopLoops();
      this.notify();
    };
    ws.onerror = () => {
      this.error = "socket";
      this.notify();
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === "string") this.onText(ev.data);
      else if (ev.data instanceof ArrayBuffer) this.onBinary(new Uint8Array(ev.data));
    };
  }

  close(): void {
    this.stopLoops();
    this.ws?.close(1000, "leave");
    this.ws = null;
  }

  /** As the room's host, ask the server to start the fight. */
  requestStart(): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify({ t: "start" }));
  }

  waitingView(): WaitingView {
    return {
      connected: this.connected,
      closed: this.closed,
      error: this.error,
      info: this.waiting,
      infoAt: this.waitingAt,
    };
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onEvent(listener: (msg: ServerControl) => void): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }

  private stopLoops(): void {
    if (this.loop) clearInterval(this.loop);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.loop = null;
    this.pingTimer = null;
  }

  // ---- inbound -----------------------------------------------------------

  private onText(text: string): void {
    const msg = parseServerControl(text);
    if (!msg) return;
    if (msg.t === "waiting") {
      this.waiting = msg;
      this.waitingAt = performance.now();
      this.error = null;
    } else if (msg.t === "joined") {
      if (!isFighterCount(msg.sessionSize)) return;
      this.waiting = null;
      this.error = null;
      this.myId = msg.id;
      this.fightStartTick = msg.fightStartTick;
      const slots = msg.fighters.map((f) => ({ nickname: f.nickname, isBot: f.isBot }));
      this.authoritative = createMatch({ sessionSize: msg.sessionSize, seed: msg.seed }, slots);
      this.predicted = cloneState(this.authoritative);
      this.localTick = msg.tick + 2;
      const me = this.authoritative.fighters[this.myId];
      if (me) this.controls.yaw = me.facing;
      this.startLoops();
    } else if (msg.t === "roster") {
      const a = this.authoritative;
      if (a) {
        for (const r of msg.fighters) {
          const f = a.fighters[r.id];
          if (f) {
            f.nickname = r.nickname;
            f.isBot = r.isBot;
          }
        }
      }
    } else if (msg.t === "pong") {
      this.rttMs = Date.now() - msg.c;
    } else if (msg.t === "error") {
      this.error = msg.code;
    }
    for (const l of this.eventListeners) l(msg);
    this.notify();
  }

  private onBinary(bytes: Uint8Array): void {
    const snap = decodeSnapshot(bytes);
    const a = this.authoritative;
    if (!snap) {
      // A snapshot this build cannot read means a build mismatch. Say so
      // instead of rendering a world that silently never moves again.
      this.error = "snapshot_decode";
      this.notify();
      return;
    }
    if (!a) return;
    this.buffer.push({ receivedAt: performance.now(), snapshot: snap });
    if (this.buffer.length > 12) this.buffer.shift();
    a.tick = snap.tick;
    a.phase = snap.phase;
    a.clockTicks = snap.clockTicks;
    a.cage.r = snap.cage.r;
    a.cage.targetR = snap.cage.targetR;
    a.cage.phase = snap.cage.phase;
    a.cage.warning = snap.cage.warning;
    a.winner = snap.winner;
    for (const sf of snap.fighters) {
      const f = a.fighters[sf.id];
      if (f) applyFighterSnapshot(f, sf);
    }
    this.lastAck = snap.ackTick;
    this.reconcile();
    if (this.localTick <= snap.tick) this.localTick = snap.tick + 1;
  }

  /** Rebuild the predicted state: authoritative + every input the server has not applied yet. */
  private reconcile(): void {
    const a = this.authoritative;
    if (!a) return;
    this.pending = this.pending.filter((i) => i.tick > this.lastAck);
    const p = cloneState(a);
    const n = a.fighters.length;
    for (const input of this.pending) {
      const inputs: (PlayerInput | null)[] = new Array(n).fill(null);
      inputs[this.myId] = present(input);
      step(p, inputs, { predictOnly: true });
    }
    this.predicted = p;
  }

  // ---- outbound ----------------------------------------------------------

  /**
   * Ticks at exactly TICK_RATE on average. A bare setInterval(1000 / 30) runs
   * every 33 ms (timers truncate), so the client made 1 % more inputs than the
   * server takes, and they piled up in its queue as ~100 ms of input delay.
   */
  private startLoops(): void {
    if (this.loop) return;
    const tickMs = 1000 / TICK_RATE;
    this.nextTickAt = performance.now();
    this.loop = setInterval(() => {
      const now = performance.now();
      let n = 0;
      while (now >= this.nextTickAt && n < MAX_CATCHUP) {
        this.clientTick();
        this.nextTickAt += tickMs;
        n += 1;
      }
      if (now >= this.nextTickAt) this.nextTickAt = now + tickMs;
    }, tickMs / 2);
  }

  /** Pings from the moment the socket opens: RTT for the HUD, and a waiting room never idles out. */
  private startPings(): void {
    if (this.pingTimer) return;
    this.pingTimer = setInterval(() => {
      if (this.ws?.readyState === WebSocket.OPEN)
        this.ws.send(JSON.stringify({ t: "ping", c: Date.now() }));
    }, 1000);
  }

  private clientTick(): void {
    const p = this.predicted;
    if (!p || !this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.localTick - this.lastAck > MAX_LEAD && this.lastAck > 0) return; // too far ahead, let the server catch up
    this.localTick += 1;
    const input = this.controls.sample(this.localTick);
    input.viewTick = this.displayedTick(performance.now() - INTERP_MS);
    this.aimAtReticle(input, p);
    this.pending.push(input);
    if (this.pending.length > MAX_PENDING) this.pending.shift();
    const recent = this.pending.slice(-3);
    this.ws.send(encodeInputs(recent));
    const inputs: (PlayerInput | null)[] = new Array(p.fighters.length).fill(null);
    inputs[this.myId] = present(input);
    step(p, inputs, { predictOnly: true });
  }

  // ---- view --------------------------------------------------------------

  /** The state to render right now: predicted local fighter, interpolated others. */
  view(now: number): ClientView | null {
    const a = this.authoritative;
    const p = this.predicted;
    if (!a || !p) return null;
    const me = p.fighters[this.myId];
    const interpolated = this.interpolate(now - INTERP_MS);
    const latest = this.buffer[this.buffer.length - 1]?.snapshot;
    const fighters: RenderFighter[] = a.fighters.map((f, i) => {
      const src = i === this.myId && me ? me : (interpolated.get(i) ?? f);
      const snap = latest?.fighters.find((sf) => sf.id === f.id);
      const local = i === this.myId && me ? me : null;
      const telegraph: Telegraph = local
        ? {
            chargeLimb: local.chargeArm?.limb ?? local.chargeLeg?.limb ?? null,
            chargeLevel: Math.min(1, ((local.chargeArm ?? local.chargeLeg)?.ticks ?? 0) / 18),
            throwingArm: local.strikeArm?.phase === "throw",
            throwingLeg: local.strikeLeg?.phase === "throw",
            windupArm: local.strikeArm?.phase === "windup",
            windupLeg: local.strikeLeg?.phase === "windup",
            parryArm: local.parry?.arm ?? null,
            staggered: local.stagger > 0,
          }
        : {
            chargeLimb: snap?.chargeLimb ?? null,
            chargeLevel: snap?.chargeLevel ?? 0,
            throwingArm: snap?.strikeArm?.phase === "throw",
            throwingLeg: snap?.strikeLeg?.phase === "throw",
            windupArm: snap?.strikeArm?.phase === "windup",
            windupLeg: snap?.strikeLeg?.phase === "windup",
            parryArm: snap?.parryArm ?? null,
            staggered: (snap?.stagger ?? 0) > 0,
          };
      return {
        id: f.id,
        nickname: f.nickname,
        alive: f.alive,
        isBot: f.isBot,
        isLocal: i === this.myId,
        guard: src.guard,
        exhausted: f.exhausted,
        telegraph,
        pos: src.pos,
        facing: src.facing,
        duck: src.duck,
        lean: src.lean,
        stance: src.stance,
        twist: src.twist,
        southpaw: src.southpaw,
        checking: src.checking,
        coverZone: local ? local.coverZone : -1,
        combo: local ? { chain: local.chain, queued: local.queue.length } : { chain: 0, queued: 0 },
        handL: src.handL,
        handR: src.handR,
        footL: src.footL,
        footR: src.footR,
        hp: f.hp,
        stamina: i === this.myId && me ? me.stamina : f.stamina,
        damageDealt: f.damageDealt,
      };
    });
    return {
      connected: this.connected,
      error: this.error,
      waiting: this.buffer.length === 0,
      phase: a.phase,
      tick: a.tick,
      clockTicks: a.clockTicks,
      fightStartTick: this.fightStartTick,
      cage: { ...a.cage },
      myId: this.myId,
      winner: a.winner,
      rttMs: this.rttMs,
      fighters,
    };
  }

  /** The two buffered snapshots around `renderTime` and how far between them it is. */
  private bracket(
    renderTime: number,
  ): { s0: BufferedSnapshot; s1: BufferedSnapshot | undefined; t: number } | null {
    const buf = this.buffer;
    if (buf.length === 0) return null;
    let i = buf.length - 1;
    while (i > 0 && (buf[i]?.receivedAt ?? 0) > renderTime) i -= 1;
    const s0 = buf[i];
    const s1 = buf[i + 1];
    if (!s0) return null;
    if (!s1) return { s0, s1, t: 1 };
    const span = s1.receivedAt - s0.receivedAt;
    const t = span > 0 ? Math.max(0, Math.min(1, (renderTime - s0.receivedAt) / span)) : 1;
    return { s0, s1, t };
  }

  /**
   * The server tick of the world drawn for remote fighters at `renderTime`, or
   * 0 before the first snapshot. Sent with each input so the server can resolve
   * strikes against what this player saw (lag compensation).
   */
  private displayedTick(renderTime: number): number {
    const b = this.bracket(renderTime);
    if (!b) return 0;
    const t0 = b.s0.snapshot.tick;
    return b.s1 ? Math.round(t0 + (b.s1.snapshot.tick - t0) * b.t) : t0;
  }

  /**
   * Points the input's look from the fighter's eyes at what the reticle is on
   * (the camera sits over the shoulder, so at fighting range the two differ
   * by ~13° down and ~18° sideways). Opponents are taken as drawn, which is
   * what lag compensation rewinds to.
   */
  private aimAtReticle(input: PlayerInput, p: MatchState): void {
    const me = p.fighters[this.myId];
    if (!me?.alive) return;
    const eye = rotateY(eyeLocal(me.duck, me.lean), Math.sin(me.facing), Math.cos(me.facing));
    const eyeWorld = { x: me.pos.x + eye.x, y: me.pos.y + eye.y, z: me.pos.z + eye.z };
    const targets: Volume[] = [];
    const drawn = this.interpolate(performance.now() - INTERP_MS);
    for (const f of this.authoritative?.fighters ?? []) {
      if (f.id === this.myId || !f.alive) continue;
      targets.push(...poseFighter(drawn.get(f.id) ?? f));
    }
    const aim = convergeAim(
      eyeWorld,
      this.controls.yaw,
      (this.controls.pitch * Math.PI) / 180,
      targets,
    );
    input.yaw = yawFromRadians(aim.yaw);
    input.pitch = Math.max(-89, Math.min(89, Math.round((aim.pitch * 180) / Math.PI)));
  }

  private interpolate(renderTime: number): Map<number, PoseLike> {
    const out = new Map<number, PoseLike>();
    const b = this.bracket(renderTime);
    if (!b) return out;
    const { s0, s1, t } = b;
    if (!s1) {
      for (const f of s0.snapshot.fighters) out.set(f.id, poseOf(f));
      return out;
    }
    const byId = new Map(s1.snapshot.fighters.map((f) => [f.id, f]));
    for (const f0 of s0.snapshot.fighters) {
      const f1 = byId.get(f0.id);
      if (!f1) {
        out.set(f0.id, poseOf(f0));
        continue;
      }
      out.set(f0.id, {
        pos: lerpVec(f0.pos, f1.pos, t),
        facing: f0.facing + wrapAngle(f1.facing - f0.facing) * t,
        duck: f0.duck + (f1.duck - f0.duck) * t,
        lean: f0.lean + (f1.lean - f0.lean) * t,
        stance: f0.stance + (f1.stance - f0.stance) * t,
        twist: f0.twist + (f1.twist - f0.twist) * t,
        southpaw: f1.southpaw,
        checking: f1.checking,
        guard: f1.guard,
        handL: lerpVec(f0.handL, f1.handL, t),
        handR: lerpVec(f0.handR, f1.handR, t),
        footL: lerpVec(f0.footL, f1.footL, t),
        footR: lerpVec(f0.footR, f1.footR, t),
      });
    }
    return out;
  }
}

interface PoseLike {
  pos: Vec3;
  facing: number;
  duck: number;
  lean: number;
  stance: number;
  twist: number;
  southpaw: boolean;
  checking: boolean;
  guard: boolean;
  handL: Vec3;
  handR: Vec3;
  footL: Vec3;
  footR: Vec3;
}

function poseOf(f: FighterSnapshot): PoseLike {
  return {
    pos: f.pos,
    facing: f.facing,
    duck: f.duck,
    lean: f.lean,
    stance: f.stance,
    twist: f.twist,
    southpaw: f.southpaw,
    checking: f.checking,
    guard: f.guard,
    handL: f.handL,
    handR: f.handR,
    footL: f.footL,
    footR: f.footR,
  };
}

/** Prediction resolves no hits, so it never rewinds: it steps with the present. */
function present(input: PlayerInput): PlayerInput {
  return input.viewTick === 0 ? input : { ...input, viewTick: 0 };
}

function lerpVec(a: Vec3, b: Vec3, t: number): Vec3 {
  return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, z: a.z + (b.z - a.z) * t };
}

function cloneState(state: MatchState): MatchState {
  return structuredClone(state);
}

export type { FighterState };
